import {
  decryptData,
  encryptData,
  encryptSecret,
  generateRandomSecretKey,
  getSecretKey,
} from "@/features/cryptography";
import { SupabaseDatabase } from "@/features/supabase";
import { store } from "@/store";
import { SeveritiesView, supabaseApi } from "@/store/services/supabase";
import { dispatchWithRetries } from "@/store/services/supabase/utils";
import type { KeyPairAndSalt } from "@/types/cryptography";
import type { ProcessStatus } from "@/types/processes";
import { arrayBufferToString, hexToArrayBuffer } from "@/utils/crypto";
import type { SeveritiesJobSchema, SeveritiesRunHazardOptions } from "../types";
import { encryptSeveritiesAsset } from "./asset";
import type { SeveritiesRow } from "./validation";

type Severities = SupabaseDatabase["public"]["Tables"]["severities"]["Row"];

/** Rows inserted into `severities_assets` per request. */
const BATCH_SIZE = 100;

interface CreateSeveritiesRunParams {
  name: string;
  rows: SeveritiesRow[];
  settings: SeveritiesRunHazardOptions;
  isPrivate: boolean;
  groupId?: string;
  /** User key for a private run, otherwise the org key. */
  publicKey: CryptoKey;
  onUploaded?: (count: number) => void;
}

/**
 * Stores a new run and its encrypted rows. Rows stay unprocessed until the pipeline posts them.
 * A failed upload deletes the partial run before rethrowing.
 */
export const createSeveritiesRun = async ({
  name,
  rows,
  settings,
  isPrivate,
  groupId,
  publicKey,
  onUploaded,
}: CreateSeveritiesRunParams) => {
  const { secret, exportedKey } = await generateRandomSecretKey();

  const run = await dispatchWithRetries<Severities>({
    action: supabaseApi.endpoints.addSeverities.initiate({
      name: await encryptData(secret, name),
      encryptedSecret: arrayBufferToString(await encryptSecret({ publicKey, secret: exportedKey })),
      groupId,
      isPrivate,
    }),
    maxRetries: 10,
    timeout: 5000,
  });

  try {
    for (let start = 0; start < rows.length; start += BATCH_SIZE) {
      const batch = await Promise.all(
        rows.slice(start, start + BATCH_SIZE).map((row) => encryptSeveritiesAsset(secret, run.id, row, settings))
      );

      await dispatchWithRetries<null>({
        action: supabaseApi.endpoints.addSeveritiesAssets.initiate(batch),
        maxRetries: 10,
        timeout: 5000,
      });
      onUploaded?.(start + batch.length);
    }

    // Seeding `pending` marks the upload as finished, so tracking can't start on a partial set of rows.
    await dispatchWithRetries<null>({
      action: supabaseApi.endpoints.updateSeveritiesProgressCounts.initiate({
        id: run.id,
        total: rows.length,
        success: 0,
        error: 0,
        pending: rows.length,
      }),
      maxRetries: 10,
      timeout: 5000,
    });
  } catch (err) {
    await store
      .dispatch(supabaseApi.endpoints.deleteSeverities.initiate(run.id))
      .unwrap()
      // Report the upload failure rather than a delete failure that masks it.
      .catch((e) => console.error("Failed to delete partially uploaded Severities run:", e));
    throw err;
  }

  store.dispatch(supabaseApi.util.invalidateTags([{ type: "Severities", id: "List" }]));

  return run;
};

export const getSeveritiesStatus = ({ pending, error, total, success, unprocessed }: SeveritiesView): ProcessStatus => {
  if (pending === 0) return error ? "completed_with_errors" : "completed";
  if (total > 0 && total === success + error) return "completed";
  // Rows not yet posted to the API mean the upload is still going.
  return unprocessed ? "uploading" : "processing";
};

export const toSeveritiesJobSchema = async (
  item: SeveritiesView,
  user: KeyPairAndSalt,
  org: KeyPairAndSalt | null
): Promise<SeveritiesJobSchema> => {
  const privateKey = item.is_private ? user.keyPair.privateKey : org?.keyPair.privateKey;
  if (!privateKey) {
    throw new Error(`Severities run ${item.id} requires ${item.is_private ? "user" : "org"} private key`);
  }

  const secret = await getSecretKey(privateKey, item.secret_enc!);
  const name = item.name ? await decryptData(secret, hexToArrayBuffer(item.name)) : "Untitled run";

  return {
    id: item.id!,
    name,
    startTime: new Date(item.created_at!).getTime(),
    status: getSeveritiesStatus(item),
    assets: {
      totalCount: item.total,
      processedCount: item.success,
      errorCount: item.error,
      unprocessedCount: item.unprocessed,
    },
    userId: item.user_id || "",
    email: item.email || "Unknown User",
    groupId: item.group_id || "",
    groupName: item.group_name || "",
    isPrivate: item.is_private || false,
    secret,
    stamps: item.stamps || [],
  };
};

/** Decrypts a page of runs, dropping any the current keys can't open. */
export const toSeveritiesJobSchemas = async (
  items: SeveritiesView[],
  user: KeyPairAndSalt,
  org: KeyPairAndSalt | null
): Promise<SeveritiesJobSchema[]> => {
  const results = await Promise.allSettled(items.map((item) => toSeveritiesJobSchema(item, user, org)));

  return results
    .filter((result): result is PromiseFulfilledResult<SeveritiesJobSchema> => result.status === "fulfilled")
    .map((result) => result.value);
};
